import { useCallback, useEffect, useState } from "react";
import { fmtDate } from "@/lib/status";
import { toast } from "sonner";
import { Building2, Plus, UserPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, errMsg } from "@/lib/api";
import { usePractice } from "@/pages/StaffApp";
import { PATIENT_STATUS } from "@/components/PatientStatusControl";

const TYPE = { snf: "SNF", alf: "ALF", il: "IL", clinic: "Clinic", telehealth: "Telehealth", other: "Other" };

function AddSite({ kind, label }) {
  const { reloadPractice } = usePractice();
  const [name, setName] = useState("");
  const add = async () => {
    try { await api.post(`/practice/${kind}`, { name }); toast.success(`${label} added`); setName(""); reloadPractice(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="flex gap-2"><Input value={name} onChange={(e) => setName(e.target.value)} placeholder={`New ${label.toLowerCase()} name`} className="h-9" data-testid={`add-${kind}-name`} />
      <Button size="sm" className="btn-brand h-9" disabled={!name.trim()} onClick={add} data-testid={`add-${kind}-btn`}><Plus className="w-4 h-4 mr-1" />Add</Button></div>
  );
}

export default function FacilitiesTab({ onOpenChart }) {
  const { practice, can, facility, scope } = usePractice();
  const [code, setCode] = useState(facility || practice.facilities[0]?.code);
  const [rows, setRows] = useState([]);
  const load = useCallback(() => { if (code) api.get(`/facilities/${code}/patients`).then((r) => setRows(r.data.items)).catch((e) => toast.error(errMsg(e))); }, [code]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (facility) setCode(facility); }, [facility]);
  const prov = (c) => practice.providers.find((p) => p.code === c)?.name || "-";
  return (
    <div className="grid lg:grid-cols-[300px_1fr] gap-4" data-testid="facilities-tab">
      <div className="space-y-2">
        <div className="max-h-[60vh] overflow-y-auto space-y-1 pr-1">{practice.facilities.map((f) => (
          <button key={f.code} onClick={() => setCode(f.code)} data-testid={`facility-pick-${f.code}`}
            className={`w-full text-left rounded-lg border px-3 py-2 text-sm transition-colors ${code === f.code ? "bg-brand text-white border-transparent shadow" : "bg-white hover:bg-slate-50"}`}>
            <Building2 className="inline w-4 h-4 mr-1.5 -mt-0.5" />{f.name}<span className="ml-1 text-[10px] uppercase opacity-80">{TYPE[f.type] || "Clinic"}</span></button>))}</div>
        {can("settings:manage") && !scope?.limited && <><AddSite kind="facilities" label="Facility" /><AddSite kind="providers" label="Provider" /></>}
      </div>
      <div className="bg-white rounded-xl border">
        <div className="px-4 py-3 border-b flex items-center gap-2"><UserPlus className="w-4 h-4 text-brand" /><h3 className="font-semibold">Patients at {practice.facilities.find((f) => f.code === code)?.name}</h3>
          <span className="ml-auto text-xs text-slate-500" data-testid="facility-patient-count">{rows.length} patients</span></div>
        <div className="divide-y max-h-[65vh] overflow-y-auto">
          {rows.map((p) => {
            const st = PATIENT_STATUS.find(([v]) => v === (p.patient_status || "active"));
            return (
              <button key={p.id} onClick={() => onOpenChart(p.id)} className="w-full text-left px-4 py-2.5 hover:bg-slate-50 flex items-center gap-3 text-sm" data-testid={`facility-patient-${p.id}`}>
                <span className="flex-1"><b>{p.last_name}, {p.first_name}</b><span className="block text-xs text-slate-500">DOB {fmtDate(p.dob)} - {p.chart_id} - {prov(p.provider_code)}</span></span>
                <span className={`text-xs px-2 py-0.5 rounded-full ${st[0] === "active" ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`}>{st[1]}</span>
              </button>
            );
          })}
          {!rows.length && <p className="p-4 text-sm text-slate-500">No patients at this facility yet.</p>}
        </div>
      </div>
    </div>
  );
}
